import { MutableRefObject, useEffect } from 'react';
import { isEqual } from 'lodash';
import { TableChangeSource, TableContentData } from '../../../../../common/table/TableTypes';
import globalLogger from '../../../../logger';
import useRefCallback from '../../../../utils/react/useRefCallback';
import useThrottledCallback from '../../../../utils/react/useThrottledCallback';
import { ActiveTableSession } from '../../bridge/store/hotTableStoreTypes';
import {
    convertHotInstanceToRaw,
    doesHotSnapshotMatchTableState,
} from '../utils/hotInstanceUtils/persistence/hotTablePersistenceUtils';
import { applyRawTableContentToHotInstance } from '../utils/hotInstanceUtils/persistence/hotTableRehydrationUtils';
import { isHotTableColumnLiveResizeInProgress } from '../utils/hotInstanceUtils/columnSize/hotTableManualColumnLiveResizeSessionUtils';
import { syncHotTableColWidthsToContainer } from '../utils/hotInstanceUtils/columnSize/hotTableColWidthSyncUtils';
import { repositionAutofillHandle } from '../utils/hotInstanceUtils/selection/hotTableSelectionUiUtils';
import usePrevious from '../../../../utils/react/usePrevious';
import type { MNHotInstance } from '../../bridge/tableHotInstanceTypes';

const logger = globalLogger.createChannel('table.active-persistence');

type UseReduxToHotRehydration = {
    elementId: string;
    tableInstance: MNHotInstance | null;
    isActive: boolean;
    gridSize: number;
    inList: string | undefined;
    hotTableContainerRef: MutableRefObject<HTMLDivElement | null>;
    getContextZoomScale: () => number;
    tableContentData: TableContentData | undefined;
    tableContentColWidthsGU: number[] | undefined;
    activeTableSession: ActiveTableSession;
};

/**
 * Handles explicit Redux -> HOT instance hydration.
 *
 * The initial persisted snapshot is applied during HOT instance construction (in HotTable.ts).
 * After that, inactive tables rehydrate on later persisted updates and active tables only reconcile
 * clean unexpected persisted updates.
 */
export const useReduxToHotRehydration = ({
    elementId,
    tableInstance,
    isActive,
    gridSize,
    inList,
    hotTableContainerRef,
    getContextZoomScale,
    tableContentData,
    tableContentColWidthsGU,
    activeTableSession,
}: UseReduxToHotRehydration): void => {
    const prevTableContentData = usePrevious(tableContentData);
    const prevTableContentColWidthsGU = usePrevious(tableContentColWidthsGU);

    const rehydrateFromPersistedState = useRefCallback(() => {
        if (!tableInstance || !tableContentData || !tableContentColWidthsGU) return false;

        const rehydratedTableContent = {
            data: tableContentData,
            colWidthsGU: tableContentColWidthsGU,
        };

        applyRawTableContentToHotInstance(
            tableInstance,
            rehydratedTableContent,
            gridSize,
            'update',
            TableChangeSource.REHYDRATE,
        );

        syncHotTableColWidthsToContainer({
            hotInstance: tableInstance,
            tableContentColWidthsGU,
            gridSize,
            inList,
            hotTableContainerRef,
            getContextZoomScale,
        });
        repositionAutofillHandle(tableInstance, hotTableContainerRef);

        return true;
    }, [
        getContextZoomScale,
        gridSize,
        hotTableContainerRef,
        inList,
        tableContentColWidthsGU,
        tableContentData,
        tableInstance,
    ]);

    /**
     * Handle persisted updates after the initial constructor-time load.
     *
     * Inactive tables always follow persisted state (though only update if there's a mismatch).
     * Active tables only reconcile unexpected clean persisted updates.
     */
    useEffect(() => {
        if (!tableInstance) return;
        if (!prevTableContentData && !prevTableContentColWidthsGU) return;

        const didPersistedDataChange = !isEqual(prevTableContentData, tableContentData);
        const didPersistedColWidthsChange = !isEqual(prevTableContentColWidthsGU, tableContentColWidthsGU);

        if (!didPersistedDataChange && !didPersistedColWidthsChange) return;

        const tableSnapshot = convertHotInstanceToRaw(tableInstance, gridSize);
        const isTableSnapshotEqual = doesHotSnapshotMatchTableState({
            tableSnapshot,
            tableContentData,
            tableContentColWidthsGU,
        });
        if (isTableSnapshotEqual) return;

        if (!isActive) {
            rehydrateFromPersistedState();
            return;
        }

        if (activeTableSession.isDirty()) {
            logger.error('Failed to reconcile unexpected element update for dirty active table', undefined, {
                elementId,
            });
            return;
        }

        logger.warn('Unexpected persisted update received for active table', { elementId });
        rehydrateFromPersistedState();
    }, [tableContentColWidthsGU, tableContentData]);

    /**
     * Keep rendered column widths aligned when the surrounding container resizes.
     */
    const throttledSyncRenderedColWidths = useThrottledCallback(
        () => {
            if (!tableInstance) return;

            syncHotTableColWidthsToContainer({
                hotInstance: tableInstance,
                tableContentColWidthsGU,
                gridSize,
                inList,
                hotTableContainerRef,
                getContextZoomScale,
            });
            repositionAutofillHandle(tableInstance, hotTableContainerRef);
        },
        100,
        [getContextZoomScale, gridSize, hotTableContainerRef, inList, tableContentColWidthsGU, tableInstance],
    );

    /**
     * Observe container-driven layout changes that require recalculating rendered column widths.
     */
    useEffect(() => {
        if (!tableInstance || !inList) return;

        const resizeObserver = new ResizeObserver(() => throttledSyncRenderedColWidths());
        resizeObserver.observe(tableInstance.rootElement);

        return () => resizeObserver.disconnect();
    }, [inList, tableInstance, throttledSyncRenderedColWidths]);

    /**
     * Re-apply rendered column widths when table sizing inputs change outside direct resize gestures.
     */
    useEffect(() => {
        if (!tableInstance) return;
        if (isHotTableColumnLiveResizeInProgress(tableInstance)) return;

        throttledSyncRenderedColWidths();
    }, [gridSize, inList, tableContentColWidthsGU, tableInstance, throttledSyncRenderedColWidths]);
};
